/**
 * CMMC focus on the Evidence library — PR A (flow F11, a11y T8-6). The
 * scorecard's "Collect evidence" and "Review N stale" land here:
 *
 *   ?program=cmmc&period=<id>&need=unassessed   "For CMMC · N requirements need evidence":
 *                                               the evidence-only requirements (the same
 *                                               set as the scorecard's +pts), titles first
 *   ?program=cmmc&period=<id>&filter=stale      "For CMMC · N Met results on stale evidence":
 *                                               the gate's stale list (same count), each with
 *                                               when it was judged and its window
 *
 *   ?program=cmmc&period=<id>&cited=<kind:id>     the evidence a result awaiting review cites
 *
 * The library's Upload / Add link (editor+) is the action. Reads: any member
 * (score, objective readiness, stale list; session-scoped, 404 off-tenant).
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Spinner } from '@aigovops/design-system';
import { getObjectiveReadiness, getProgramScore, getUnifiedEvidence, listStaleResults } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { fmtShort, plural, reqRef } from '../../lib/cmmc-model';
import { programTitle } from '../../lib/nav-config';
import { UploadForObjective } from './CmmcAssess';
import { staleLine } from '../../lib/cmmc-assess-model';
import '../../pages/cmmc.css';

const KIND: Record<string, string> = { check: 'Check', artifact: 'Document', document: 'Linked document', interview: 'Interview', sample: 'Sample', reconciliation: 'Reconciliation', population: 'Population', attestation: 'Attestation' };

export function CmmcEvidenceFocus({ slug, program, periodId, mode, cited, canUpload = false, returnTo = null }: {
  slug: string; program: string; periodId: string; mode: 'unassessed' | 'stale' | 'cited'; cited?: string | null;
  /** V102-8-3: rows offer "Upload for 3.x.x" (owner / admin / editor; server-enforced). */
  canUpload?: boolean;
  /** F19: the page that sent us here (validated by the caller to this workspace). */
  returnTo?: string | null;
}) {
  const score = useQuery({ queryKey: queryKeys.programScore(slug, program, periodId), queryFn: () => getProgramScore(slug, program, periodId), enabled: mode === 'unassessed', retry: false, staleTime: 30_000 });
  const stale = useQuery({ queryKey: queryKeys.programStale(slug, program, periodId), queryFn: () => listStaleResults(slug, program, periodId), enabled: mode === 'stale', retry: false, staleTime: 30_000 });
  const library = useQuery({ queryKey: ['evidence-unified', slug, periodId], queryFn: () => getUnifiedEvidence(slug, { limit: 200 }), enabled: mode === 'cited', retry: false, staleTime: 30_000 });
  const readiness = useQuery({ queryKey: queryKeys.objectiveReadiness(slug, program, periodId), queryFn: () => getObjectiveReadiness(slug, program, periodId), retry: false, staleTime: 60_000 });
  const titles = new Map((readiness.data?.readiness.requirements ?? []).map((r) => [r.requirement_id, r.title] as const));
  const title = (id: string) => titles.get(id.replace(/\[[^\]]*\]$/, '')) ?? reqRef(id);
  const label = programTitle(program);
  const back = returnTo ?? `/w/${slug}/app/programs/${encodeURIComponent(program)}/prepare`;
  const backLabel = returnTo && /[?&]view=gaps/.test(returnTo) ? `${programTitle(program)} Gaps` : programTitle(program);
  const q = mode === 'stale' ? stale : mode === 'cited' ? library : score;
  if (q.isLoading) return <Spinner label={`Loading what ${label} needs`} />;
  if (q.error) return <Alert variant="error"><p>{`What ${label} needs could not load.`}</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;

  let heading: string;
  let rows: Array<{ id: string; name: string; code: string; meta: string; uploadFor?: Array<{ id: string; ref: string }> }>;
  // The requirement's objectives an upload binds to (no-evidence ones first; stale: the stale objective).
  const objectivesOf = (reqId: string) => {
    const r = (readiness.data?.readiness.requirements ?? []).find((x) => x.requirement_id === reqId);
    if (!r) return [];
    return [...r.objectives.filter((o) => o.status === 'no_evidence'), ...r.objectives.filter((o) => o.status !== 'no_evidence')].map((o) => ({ id: o.objective_id, ref: o.ref }));
  };
  if (mode === 'cited') {
    const [kind, id] = (cited ?? '').split(':');
    const hit = (library.data?.evidence ?? []).find((e) => e.id === id);
    heading = `For ${label} · cited evidence`;
    rows = [{ id: id ?? 'cited', name: hit ? hit.title || hit.control_id || 'Evidence' : `${KIND[kind ?? ''] ?? 'Evidence'} · not in the library yet`, code: '',
      meta: hit ? [hit.status, fmtShort(hit.timestamp)].filter(Boolean).join(' · ') : '' }];
  } else if (mode === 'stale') {
    const list = stale.data?.stale ?? [];
    heading = `For ${label} · ${plural(list.length, 'Met result', 'Met results')} on stale evidence`;
    rows = list.map((s) => ({ id: s.determination_id, name: title(s.objective_id), code: reqRef(s.objective_id),
      uploadFor: [{ id: s.objective_id, ref: s.objective_id.slice(s.objective_id.lastIndexOf(':') + 1) }],
      // One stale-date format everywhere (the Gaps line).
      meta: staleLine(s.judged_at, s.window_days, new Date().toISOString().slice(0, 10)).text }));
  } else {
    const sc = score.data?.score;
    if (sc && !sc.level2) {
      // Level 1: the FAR ids, and the same set Gaps counts as "need evidence" (every linked row without
      // a result is missing evidence — a row awaiting review is not counted here).
      const statusOf = new Map((readiness.data?.readiness.requirements ?? []).map((r) => [r.requirement_id, r.status] as const));
      const need = sc.level1.requirements.filter((q) => q.status !== 'met'
        && q.l2.some((id) => statusOf.get(id) === 'no_evidence') && !q.l2.some((id) => statusOf.get(id) === 'not_met' || statusOf.get(id) === 'undetermined'));
      heading = `For ${label} · ${plural(need.length, 'requirement needs', 'requirements need')} evidence`;
      rows = need.map((q) => ({ id: q.requirement_id, name: title(q.l2[0] ?? q.requirement_id), code: reqRef(q.requirement_id), meta: '',
        uploadFor: q.l2.filter((id) => statusOf.get(id) === 'no_evidence').flatMap(objectivesOf) }));
    } else {
      const ids = sc?.level2
        ? sc.level2.not_yet_assessed.filter((id) => sc.level2!.closed_by_collection_alone.includes(id))
        : sc?.level1.not_yet_assessed ?? [];
      const points = new Map((sc?.level2?.unmet ?? []).map((u) => [u.requirement_id, u.points] as const));
      heading = `For ${label} · ${plural(ids.length, 'requirement needs', 'requirements need')} evidence`;
      rows = ids.map((id) => ({ id, name: title(id), code: reqRef(id), meta: points.has(id) ? `${points.get(id)} pt${points.get(id) === 1 ? '' : 's'}` : '', uploadFor: objectivesOf(id) }));
    }
  }
  return (
    <section className="gp-hero" aria-labelledby="cmmc-ev-h" data-part="cmmc-evidence-focus" data-mode={mode}>
      <h2 id="cmmc-ev-h" className="gp-sec-h">{heading}</h2>
      {rows.length ? (
        <ul className="gp-cmmc-rows" aria-label={heading}>
          {rows.map((r) => (
            <li key={r.id}>
              <span>{r.name}</span>
              {r.code && r.code !== r.name ? <span className="gp-dim gp-cmmc-code">{r.code}</span> : null}
              {r.meta ? <span className="gp-cmmc-meta">{r.meta}</span> : null}
              {canUpload && r.uploadFor?.length
                ? <UploadForObjective slug={slug} program={program} periodId={periodId} refLabel={r.code || r.name} objectives={r.uploadFor}
                    label={mode === 'stale' ? `Upload new for ${r.code}` : `Upload for ${r.code}`} />
                : null}
            </li>
          ))}
        </ul>
      ) : <p className="gp-dim">Nothing outstanding.</p>}
      <p><Link className="gp-cmmc-back" to={back} data-part="focus-back">{`Back to ${backLabel}`}</Link></p>
    </section>
  );
}
